import axios from 'axios';
import { getIn } from 'formik';
import * as yup from 'yup';
import { ObjectSchema } from 'yup';
import { IShipmentService, PostalCodeVerification } from '../../@posturinn/client-react';
import {
	GET_SHIPMENT,
	MOBIZ_API_URL,
	VALIDATION_COMPANY_SOCIALID,
	VALIDATION_SOCIALID,
	VERIFY_EMAIL_URL,
} from '../../constants/paths/api';
import store from '../../store';
import { clientIdConfig } from '../mobizHelpers/configurations';
import {
	dhlShipmentTrackingNumberRegex,
	domesticShipmentPhoneRegex,
	icelandicMobileShipmentPhoneRegex,
	icelandicShipmentPhoneRegex,
	icelandicShipmentPhoneWithCountryCodeRegex,
	internationalShipmentPhoneWithDoubleZeroRegex,
	internationalShipmentPhoneWithPlusRegex,
	noIcelandicLettersInEmailRegex,
	posturinnAndExternalShipmentsId,
	posturinnReturnedShipmentTrackingNumberRegex,
	posturinnShipmentTrackingNumberRegex,
	trackingNumberRegex,
} from '../regex/regex';

const validSsnMapper = {};
const invalidSsnMapper = {};
const invalidCompanySsnMapper = {};
const validCompanySsnMapper = {};
const companySsnValidationResponseMapper = {};
const companySsnValidationRequestMapper = {};
const validPostCodeMapper = {};
const invalidPostCodeMapper = {};
const invalidTrackingnumberFromSapMapper = {};
const invalidOutboundTrackingnumberFromSapMapper = {};

async function fetchCompanySsnValidation(value) {
	const trimmedValue = value?.trimEnd();

	if (!trimmedValue) return null;
	if (companySsnValidationResponseMapper[trimmedValue]) {
		return companySsnValidationResponseMapper[trimmedValue];
	}
	if (companySsnValidationRequestMapper[trimmedValue]) {
		return companySsnValidationRequestMapper[trimmedValue];
	}

	companySsnValidationRequestMapper[trimmedValue] = axios(
		`${VALIDATION_COMPANY_SOCIALID}?identificationNumber=${trimmedValue}`
	)
		.then(({ data }) => {
			companySsnValidationResponseMapper[trimmedValue] = data;
			delete companySsnValidationRequestMapper[trimmedValue];
			return data;
		})
		.catch(error => {
			delete companySsnValidationRequestMapper[trimmedValue];
			throw error;
		});

	return companySsnValidationRequestMapper[trimmedValue];
}

export async function validateSsn(value) {
	if (!value || value.length === 0) return true;
	if (!value || value.length !== 10 || invalidSsnMapper[value]) return false;
	if (validSsnMapper[value]) return true;

	const response = await axios(`${VALIDATION_SOCIALID}?identificationNumber=${value}`);

	if (response.data.error) {
		invalidSsnMapper[value] = true;
		return false;
	}

	validSsnMapper[value] = true;
	return true;
}

export async function validateSsnOrEmpty(value) {
	if (value.length === 0) return true;
	return validateSsn(value);
}

export async function validateCompanySsn(value) {
	const trimmedValue = value?.trimEnd();

	if (!trimmedValue || trimmedValue.length < 9) return true;
	if (invalidCompanySsnMapper[trimmedValue]) return false;
	if (validCompanySsnMapper[trimmedValue]) return true;

	const response = await fetchCompanySsnValidation(trimmedValue);

	if (!response.isValid) {
		invalidCompanySsnMapper[trimmedValue] = true;
		return false;
	}

	validCompanySsnMapper[trimmedValue] = true;
	return true;
}

export async function getCompanyNameFromSsn(value) {
	const trimmedValue = value?.trimEnd();

	if (!trimmedValue || trimmedValue.length < 9) return '';

	const response = await fetchCompanySsnValidation(trimmedValue);
	return response.businessName;
}

export async function validateForeignPostcodeGetCountryFromStore(value, createError, path) {
	const {
		registerShipmentReducer: { stepInfoFormInfo: typeFormInfo },
	} = store.getState();
	return validateForeignPostcode(value, typeFormInfo.country.value, createError, path);
}

export async function validateForeignPostcode(value, country, createError, path) {
	if (!value || invalidPostCodeMapper[`${value}-${country}`]) {
		return createError({
			message: invalidPostCodeMapper[`${value}-${country}`],
			path,
		});
	}
	if (validPostCodeMapper[`${value}-${country}`]) return true;

	const { injector } = store.getState().mobizReducer;
	const shipmentService: IShipmentService = injector.get(IShipmentService);
	return shipmentService.verifyPostalCode(country, value).subscribe({
		next: (response: PostalCodeVerification) => {
			if (response.valid) {
				validPostCodeMapper[`${value}-${country}`] = true;
				return true;
			}
				invalidPostCodeMapper[`${value}-${country}`] = response.invalidMessage;
				return createError({
					message: response.invalidMessage,
					path,
				});
		},
		error: error => {
			return createError({
				message: error.info.details,
				path,
			});
		},
	});
}


export function validateShipmentTrackingNumber(shipmentTrackingNumber?: string): boolean {
	if (!shipmentTrackingNumber) return false;
	const trimmedTrackingNumber = shipmentTrackingNumber.trim();
	return (
		posturinnShipmentTrackingNumberRegex.test(trimmedTrackingNumber) ||
		posturinnReturnedShipmentTrackingNumberRegex.test(trimmedTrackingNumber) ||
		dhlShipmentTrackingNumberRegex.test(trimmedTrackingNumber) ||
		trackingNumberRegex.test(trimmedTrackingNumber)
	);
}

export function validateShipmentTrackingNumberExternal(shipmentTrackingNumber: string): boolean {
	return posturinnAndExternalShipmentsId.test(shipmentTrackingNumber);
}

export function isValidShipmentPhone(phone?: string): boolean {
	const value = (phone || '').trim();

	if (isIcelandicShipmentPhone(value)) {
		return icelandicMobileShipmentPhoneRegex.test(value);
	}

	return (
		domesticShipmentPhoneRegex.test(value) ||
		internationalShipmentPhoneWithPlusRegex.test(value) ||
		internationalShipmentPhoneWithDoubleZeroRegex.test(value)
	);
}

export function isValidSenderShipmentPhone(phone?: string): boolean {
	const value = (phone || '').trim();

	if (isIcelandicShipmentPhone(value)) {
		return icelandicShipmentPhoneRegex.test(value);
	}

	return isValidShipmentPhone(value);
}

export function isValidIcelandicShipmentPhone(phone?: string): boolean {
	const value = (phone || '').trim();
	return icelandicMobileShipmentPhoneRegex.test(value);
}

export function isIcelandicShipmentPhone(phone?: string): boolean {
	const value = (phone || '').trim();
	return icelandicShipmentPhoneRegex.test(value) || icelandicShipmentPhoneWithCountryCodeRegex.test(value);
}

export function isShipmentPhoneMissingInternationalPrefix(phone?: string): boolean {
	const value = (phone || '').trim();
	return value.length > 7 && !value.startsWith('+') && !value.startsWith('00');
}

export function isValidOptionalShipmentPhone(phone?: string): boolean {
	const value = (phone || '').trim();
	return !value || isValidShipmentPhone(value);
}

export function normalizeShipmentPhoneForMobiz(phone?: string): string {
	const value = (phone || '').replace(/\s/g, '');

	if (!value) return '';
	if (icelandicMobileShipmentPhoneRegex.test(value)) {
		return value.replace(/^(\+354|00354)/, '');
	}

	return value;
}

export function normalizeInternationalShipmentPhoneForMobiz(phone?: string): string {
	const value = (phone || '').replace(/\s/g, '');

	if (!value) return '';
	if (icelandicShipmentPhoneRegex.test(value) && !value.startsWith('+') && !value.startsWith('00')) {
		return `+354${value}`;
	}

	return value;
}

export function normalizeShipmentPhoneForDestination(phone?: string, countryCode?: string): string {
	return (countryCode || '').toUpperCase() === 'IS'
		? normalizeShipmentPhoneForMobiz(phone)
		: normalizeInternationalShipmentPhoneForMobiz(phone);
}

export async function validateTrackingnumberFromSap(trackingnumber) {
	if (!trackingnumber || trackingnumber.length < 8 || trackingnumber.length > 35) {
		return false;
	}
	if (invalidTrackingnumberFromSapMapper[trackingnumber]) {
		return true;
	}
	if (invalidTrackingnumberFromSapMapper[trackingnumber] === false) {
		return false;
	}
	const response = await axios(GET_SHIPMENT.replace('{shipmentId}', trackingnumber));
	if (response.status !== 200) {
		invalidTrackingnumberFromSapMapper[trackingnumber] = false;
		return false;
	}
	if (
		response.data.shipment.id[0] !== 'C' &&
		response.data.shipment.id[0] !== 'R' &&
		response.data.shipment.id[0] !== 'L'
	) {
		return false;
	}

	invalidTrackingnumberFromSapMapper[trackingnumber] = true;
	return true;
}

export async function validateOutboundTrackingNumber(trackingnumber) {
	if (!trackingnumber || trackingnumber.length < 8 || trackingnumber.length > 35) {
		return false;
	}
	if (invalidOutboundTrackingnumberFromSapMapper[trackingnumber]) {
		return true;
	}
	if (invalidOutboundTrackingnumberFromSapMapper[trackingnumber] === false) {
		return false;
	}

	const response = await axios(GET_SHIPMENT.replace('{shipmentId}', trackingnumber));
	if (response.status !== 200) {
		invalidOutboundTrackingnumberFromSapMapper[trackingnumber] = false;
		return false;
	} if (!(response.data.shipment.direction === 'Outbound' && !response.data.shipment.isDomestic)) {
		invalidOutboundTrackingnumberFromSapMapper[trackingnumber] = false;
		return false;
	}
	if (
		response.data.shipment.id[0] !== 'C' &&
		response.data.shipment.id[0] !== 'R' &&
		response.data.shipment.id[0] !== 'L'
	) {
		return false;
	}

	invalidCompanySsnMapper[trackingnumber] = true;
	return true;
}

export const isRequiredField = (validationSchema, name) =>
	!!getIn(validationSchema.describe().fields, name).tests.find(testName => testName === 'required');

export const getFieldPlaceholder = (placeholder: string, name: string, schema: ObjectSchema): string => {
	return placeholder + (isRequiredField(schema, name) ? '*' : '');
};

export interface ICodErrorItem {
	valid: boolean;
	message: string;
}

export interface ICodValidationResponse {
	valid: boolean;
	errorItems: {
		accOwner: ICodErrorItem;
		account: ICodErrorItem;
		accountType: ICodErrorItem;
		amount: ICodErrorItem;
		bank: ICodErrorItem;
	};
}

export async function validateEmail(email: string, t: any, translationFile: string = '') {
	if (!email || email.length === 0) return null;

	const schema = yup
		.string()
		.email('step-info.email-format')
		.matches(noIcelandicLettersInEmailRegex, 'step-info.no-icelandic-letters');

	if (!(await schema.isValid(email, schema))) return null;

	const response = await axios(
		`
	${MOBIZ_API_URL}${VERIFY_EMAIL_URL}
	?email=${email}
	`,
		clientIdConfig
	);

	const { status, deliverable, didYouMean } = response.data;
	if (translationFile && translationFile.length > 0) {
		translationFile += ':';
	}

	if (status === 'error') {
		return t(`${translationFile}step-info.email-invalid`);
	} if (status === 'success' && !deliverable && didYouMean.length > 0) {
		return t(`${translationFile}step-info.email-spell-warning`, { email: didYouMean });
	} if (status === 'success' && !deliverable) {
		return t(`${translationFile}step-info.email-undeliverable`);
	}
		return null;
	
}
